import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Input, ViewChild } from '@angular/core';
import { NgOtpInputComponent } from 'ng-otp-input';
import { NgxSmartModalService } from 'ngx-smart-modal';

import { FlagPhoneInputComponent, Phone } from '../flag-phone-input/flag-phone-input.component';
import { Table } from '../../domain/models/table';
import { User } from '../../domain/models/user';
import { ConfigService } from '../../services/config.service';
import { TableService } from '../../domain/services/table.service';
import { UserService } from '../../domain/services/user.service';
import { AppBarComponent } from '../app-bar/app-bar.component';
import { StatusMessageComponent } from '../status-message/status-message.component';

@Component({
  selector: 'app-auth-modal',
  imports: [
    CommonModule,
    AppBarComponent,
    StatusMessageComponent,
    FlagPhoneInputComponent,
  ],
  templateUrl: './auth-modal.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AuthModalComponent {

  @ViewChild(NgOtpInputComponent, { static: false }) otpInput: NgOtpInputComponent;
  @ViewChild('birthdayField') birthdayField: ElementRef;

  @Input() modalTitle: string;
  @Input() step: 'phone' | 'name' = 'phone';
  @Input() user: User | null = null;
  @Input() origin: 'PAY' | 'MENU' | 'RECEIPT' = 'PAY';

  readonly defaultPhoneInlineErrorMessage: string = 'Informe um número de celular válido.';

  phoneDataIsValid: boolean = false;
  userExists: boolean | null = null;
  submitting: boolean = false;

  phone: string;
  country: string;
  name: string | undefined;
  email: string = '';
  birthday: string = '';
  hasFullRegistration: boolean = false;
  showCompleteProfileCard: boolean = false;
  idi: number | 0;
  tableCartId: number | 0;
  phoneInlineErrorMessage: string | null = null;
  authenticationMethod: 'sms' | 'whatsapp' = 'sms';

  emailState: 'default' | 'error' | 'success' = 'default';
  emailMessage: string = '';
  birthdayState: 'default' | 'error' | 'success' = 'default';
  birthdayMessage: string = '';

  constructor(
    private ngxSmartModalService: NgxSmartModalService,
    private userService: UserService,
    private tableService: TableService,
    private configService: ConfigService,
  ) { }

  ngOnInit(): void {
    this.hasFullRegistration = this.configService.isEnabled('modulo_full_registration_waiter');
    // Get table data
    this.tableService.$openedTable.subscribe(
      (table: Table | null) => {
        if (table) {
          this.idi = table.idi;
          this.tableCartId = table.tableCartId;
        }
      }
    )
  }

  get completeProfileFormValid(): boolean {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    const birthdayValid = /^\d{2}\/\d{2}\/\d{4}$/.test(this.birthday);
    return emailValid && birthdayValid;
  }

  handlePhoneData(data: Phone): void {
    this.phone = data.phone || '';
    this.country = data.country_code;

    if (data.phone?.replace(/\D/g, '').length == data.minPhoneSize) {
      this.phoneDataIsValid = true;
      this.phoneInlineErrorMessage = null;
    } else {
      this.phoneDataIsValid = false;
      this.phoneInlineErrorMessage = this.defaultPhoneInlineErrorMessage;
    }
  }

  handleCloseDialog(): void {
    this.ngxSmartModalService.getModal('auth-modal').onClose.emit({
      userExists: this.userExists,
      authenticationMethod: this.authenticationMethod,
    });

    if (this.name) {
      this.ngxSmartModalService.getModal('auth-modal').onClose.emit({
        authenticationMethod: this.authenticationMethod,
        name: this.name,
        phone: this.phone,
        country: this.country,
        idi: this.idi,
        tableCartId: this.tableCartId,
        validated: true,
      });

      this.ngxSmartModalService.getModal('auth-modal').close();
      return;
    }
    this.ngxSmartModalService.getModal('auth-modal').onClose.subscribe((res: any) => {

    });
  }

  handleNameChange(event: any): void {
    this.name = event.target.value;
  }

  handleEmailChange(event: any): void {
    this.email = (event?.target?.value ?? '').trim();
    this.validateEmail();
  }

  handleBirthdayInput(event: Event): void {
    const mntField = this.birthdayField?.nativeElement;
    const input: HTMLInputElement | null =
      mntField?.shadowRoot?.querySelector('input') ?? (event.target as HTMLInputElement);

    const rawValue = input?.value ?? '';
    const formatted = this.formatBirthday(rawValue);

    this.birthday = formatted;
    if (mntField && mntField.value !== formatted) mntField.value = formatted;
    if (input && input.value !== formatted) {
      input.value = formatted;
      input.setSelectionRange(formatted.length, formatted.length);
    }

    this.validateBirthday();
  }

  private formatBirthday(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
      .filter(Boolean)
      .join('/');
  }

  private validateEmail(): void {
    if (!this.email) {
      this.emailState = 'default';
      this.emailMessage = '';
      return;
    }
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    this.emailState = valid ? 'success' : 'error';
    this.emailMessage = valid ? '' : 'Informe um e-mail válido';
  }

  private validateBirthday(): void {
    if (this.birthday.length < 10) {
      this.birthdayState = 'default';
      this.birthdayMessage = '';
      return;
    }
    if (this.isValidBirthday(this.birthday)) {
      this.birthdayState = 'success';
      this.birthdayMessage = '';
    } else {
      this.birthdayState = 'error';
      this.birthdayMessage = 'Informe uma data válida';
    }
  }

  private isValidBirthday(value: string): boolean {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return false;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    return (
      year >= 1900 &&
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day &&
      date <= new Date()
    );
  }

  handleCompleteProfile(): void {
    this.performLogin();
  }

  handleBackToHome(): void {
    this.ngxSmartModalService.getModal('auth-modal').close();
  }

  handleContinue(): void {
    this.submitting = true;

    this.userService.preAuth(this.country, this.phone, this.idi).subscribe({
      next: (response) => {
        const existingEmail = response.data?.email;
        const existingBirthday = response.data?.data_nascimento;

        if (this.hasFullRegistration && (!existingEmail || !existingBirthday)) {
          if (existingEmail) this.email = existingEmail;
          if (existingBirthday) {
            const datePart = (existingBirthday as string).split('T')[0];
            const [y, m, d] = datePart.split('-');
            this.birthday = `${d}/${m}/${y}`;
          }
          this.showCompleteProfileCard = true;
        } else {
          this.performLogin();
        }
        this.submitting = false;
      },
      error: (e) => {
        if (e.status === 404) {
          this.step = 'name';
        } else {
          this.phoneInlineErrorMessage = this.defaultPhoneInlineErrorMessage;
          this.phoneDataIsValid = false;
        }

        this.submitting = false;
      },
    });
  }

  handleRegister(): void {
    this.performRegister();
  }

  private performRegister(): void {
    if (!this.phone || !this.country) {
      this.phoneDataIsValid = false;
      console.error('[AUTH] No name, phone or country data available');
      return;
    }

    if (this.name) {
      this.userService.register(this.country, this.phone, this.name, this.idi, this.tableCartId).subscribe({
        next: (response) => {
          if (this.hasFullRegistration) {
            this.step = 'phone';
            this.showCompleteProfileCard = true;
          } else {
            this.performLogin();
          }
        },
        error: (e) => {
          console.error('[AUTH] Error while register:', e);
          this.phoneInlineErrorMessage = this.defaultPhoneInlineErrorMessage;
          this.phoneDataIsValid = false;
        },
      });
    }
  }

  private birthdayToIso(value: string): string | undefined {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return undefined;
    const [, day, month, year] = match;
    return `${year}-${month}-${day}T00:00:00`;
  }

  private performLogin(): void {
    this.userService.login(this.country, this.phone, this.idi, this.tableCartId).subscribe({
      next: (response) => {
        if (this.showCompleteProfileCard) {
          const clientId = typeof response.data.clientId === 'number' ? response.data.clientId : undefined;
          this.userService.updateProfile(
            this.phone,
            this.idi,
            this.email,
            this.birthdayToIso(this.birthday),
            clientId,
          ).subscribe({
            error: (e) => console.error('[AUTH] Error updating profile:', e),
          });
        }

        this.ngxSmartModalService.getModal('auth-modal')?.onClose.emit({
          authenticationMethod: this.authenticationMethod,
          id: response.data.id,
          accessToken: response.data.access_token,
          expiresIn: response.data.expires_in,
          clientId: response.data.clientId,
          name: response.data.name,
          phone: this.phone,
          country: this.country,
          idi: this.idi,
          tableCartId: response.data.tableCartId ?? this.tableCartId,
          validated: true,
        });
        this.ngxSmartModalService.getModal('auth-modal').close();
      },
      error: (e) => {
        this.phoneDataIsValid = false;
        this.phoneInlineErrorMessage = this.defaultPhoneInlineErrorMessage;
      },
    });
  }
}
